import { ChevronRight } from 'lucide-preact';
import { route } from 'preact-router';
import { Badge } from '../../components/ui/Badge.js';
import { Card } from '../../components/ui/Card.js';
import { type IdentityStatus, statusView } from './identity/status.js';

/**
 * 154 (#22): what the customer's own account has and has not been verified for.
 *
 * TWO INDEPENDENT FACTS, deliberately shown as two rows rather than one combined state:
 * `email_verified_at` (they clicked the link in their email) and `identity_verification.status`
 * (staff reviewed their documents). #20 fixed the admin side of exactly this confusion — a screen
 * that reported one fact for both.
 *
 * The ticket's mockup shows another product's three-tier scheme (Basic / Intermediate /
 * Advanced) over a progress bar. CARii has two facts and no tiers, so the intent is taken — a
 * verification card on the account screen — and the tiers are not invented.
 *
 * Identity wording comes from `identity/status.ts` (059), so a customer reads the same word for
 * the same stored value here and on the documents screen itself.
 */

interface VerificationCardProps {
  emailVerifiedAt?: string | null;
  /** Absent on most accounts — the sub-document is written on first upload. */
  identityStatus?: IdentityStatus;
}

function Row({
  label,
  badge,
  onOpen,
}: {
  label: string;
  badge: preact.JSX.Element;
  onOpen?: () => void;
}) {
  const content = (
    <>
      <span className="text-sm text-gray-300">{label}</span>
      <span className="flex items-center gap-1">
        {badge}
        {onOpen ? <ChevronRight size={16} aria-hidden="true" className="text-gray-500" /> : null}
      </span>
    </>
  );

  if (!onOpen) {
    return (
      <div data-testid="verification-row" className="flex items-center justify-between py-2">
        {content}
      </div>
    );
  }

  return (
    <button
      type="button"
      data-testid="verification-row"
      onClick={onOpen}
      className="flex w-full items-center justify-between py-2 text-left"
    >
      {content}
    </button>
  );
}

export function VerificationCard({ emailVerifiedAt, identityStatus }: VerificationCardProps) {
  const emailVerified = Boolean(emailVerifiedAt);
  // An account that has never uploaded anything has no sub-document at all, which is the same
  // situation as `pending` — nothing submitted — and reads that way rather than as an error.
  const identity = statusView(identityStatus ?? 'pending');

  return (
    <Card className="mb-6">
      <div data-testid="verification-card">
        <h2 className="text-sm font-semibold text-text mb-1">Verification</h2>
        <div className="divide-y divide-white/5">
          <Row
            label="Email address"
            badge={
              <Badge variant={emailVerified ? 'success' : 'default'}>
                {emailVerified ? 'Verified' : 'Not verified'}
              </Badge>
            }
          />
          {/* Tappable, because this one the customer can act on: the documents screen is where
              they upload, re-upload after a rejection, or see why a review is outstanding. */}
          <Row
            label="Identity documents"
            badge={<Badge variant={identity.variant}>{identity.label}</Badge>}
            onOpen={() => route('/account/identity')}
          />
        </div>
      </div>
    </Card>
  );
}
